import { AppDispatch, AppThunk, GetState } from '@om-fe/core/core/types';
import {
    apiSubscribeAction,
    apiUnsubscribeAction,
    attachReducerAction,
    detachReducerAction,
} from '@om-fe/core/utils/action';
import { filterNullParams } from '@om-fe/core/utils/router';
import * as shallowEqual from 'shallowequal';
import getSlice from '@om-fe/core/utils/get-slice';
import {
    FavoriteMatchListActionsTypes,
    FavoriteMatchListSetFilterDataAction,
    FavoriteMatchListSetLoadingData,
    FavoriteMatchListStoreModel,
    FavoriteMatchListTopic,
} from './favorite-match-list.constants';
import { getFavoriteMatchListSliceName, getFavoriteMatchListTopic } from './favorite-match-list.utils';
import { favoriteMatchListReducerCreator } from './favorite-match-list.reducer';

export const favoriteMatchListSetLoadingData = (slice: string, isLoadingMoreData: boolean = false): FavoriteMatchListSetLoadingData => ({
    type: FavoriteMatchListActionsTypes.SetLoadingData,
    meta: {
        slice,
    },
    payload: {
        isLoadingMoreData,
    },
});

export const favoriteMatchListSetFilterData = (
    slice: string,
    matchId: string,
    mainMarketsLimit?: number,
): FavoriteMatchListSetFilterDataAction => ({
    type: FavoriteMatchListActionsTypes.SetFilterData,
    meta: {
        slice,
    },
    payload: {
        matchId,
        mainMarketsLimit,
    },
});

export const getFavoriteMatchList = (
    instanceId: string,
    matchId: string,
    mainMarketsLimit: number | undefined,
): AppThunk => (dispatch: AppDispatch) => {
    const slice: string = getFavoriteMatchListSliceName(instanceId);
    const topic: FavoriteMatchListTopic = getFavoriteMatchListTopic(
        matchId,
        mainMarketsLimit,
    );

    dispatch(attachReducerAction(slice, favoriteMatchListReducerCreator(instanceId)));

    dispatch(favoriteMatchListSetFilterData(
        slice,
        matchId,
        mainMarketsLimit,
    ));

    dispatch(apiSubscribeAction(topic.path, filterNullParams(topic.params), slice));
};

export const removeFavoriteMatchList = (
    instanceId: string,
): AppThunk => (dispatch: AppDispatch, getState: GetState) => {
    const slice: string = getFavoriteMatchListSliceName(instanceId);
    const favoriteMatchListSlice: FavoriteMatchListStoreModel | undefined = getSlice<FavoriteMatchListStoreModel>(getState(), slice);

    if (!favoriteMatchListSlice) {
        return;
    }

    const topic: FavoriteMatchListTopic = getFavoriteMatchListTopic(
        favoriteMatchListSlice.matchId,
        favoriteMatchListSlice.mainMarketsLimit,
    );

    dispatch(detachReducerAction(slice));
    dispatch(apiUnsubscribeAction(topic.path, filterNullParams(topic.params), slice));
};

export const updateFavoriteMatchList = (
    instanceId: string,
    matchId: string,
    mainMarketsLimit: number | undefined,
): AppThunk => (dispatch: AppDispatch, getState: GetState): void => {
    const slice: string = getFavoriteMatchListSliceName(instanceId);
    const favoriteMatchListSlice: FavoriteMatchListStoreModel | undefined = getSlice<FavoriteMatchListStoreModel>(getState(), slice);

    if (!favoriteMatchListSlice) {
        return;
    }

    const oldTopic: FavoriteMatchListTopic = getFavoriteMatchListTopic(
        favoriteMatchListSlice.matchId,
        favoriteMatchListSlice.mainMarketsLimit,
    );

    const newTopic: FavoriteMatchListTopic = getFavoriteMatchListTopic(
        matchId,
        mainMarketsLimit,
    );

    const isLoadingMoreData: boolean = shallowEqual(oldTopic, newTopic);
    dispatch(favoriteMatchListSetLoadingData(slice, isLoadingMoreData));

    dispatch(favoriteMatchListSetFilterData(
        slice,
        matchId,
        mainMarketsLimit,
    ));

    dispatch(apiUnsubscribeAction(oldTopic.path, filterNullParams(oldTopic.params), slice));
    dispatch(apiSubscribeAction(newTopic.path, filterNullParams(newTopic.params), slice));
};
